import React, { useState } from 'react';
import { Plus, Edit, Trash2, Users, Download, RefreshCw, RefreshCcw, Clock, AlertTriangle, Mail, Upload } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { DataTable, TablePagination } from '@/components/shared/DataTable';
import { SearchFilter } from '@/components/shared/SearchFilter';
import { useBookings } from '@/hooks/useBookings';
import { useRooms } from '@/hooks/useRooms';
import { useRoomTypes } from '@/hooks/useRoomTypes';
import { useCars } from '@/hooks/useCars';
import { formatCurrency, formatDate } from '@/utils/format';
import BookingDialog from '@/components/admin/bookings/BookingDialog';
import { toast } from 'sonner';

/**
 * Bookings Management Component
 * Displays bookings table with search, filter, sort, and pagination
 */
export const BookingsManagement = ({ API, initialSearchQuery = '', onSearch, onClearSearch, onAddBooking }) => {
  const {
    paginatedBookings,
    filteredBySource,
    loading,
    searchQuery,
    setSearchQuery,
    searchFilter,
    setSearchFilter,
    sourceFilter,
    setSourceFilter,
    statusFilter,
    setStatusFilter,
    sortField,
    sortOrder,
    toggleSort,
    currentPage,
    setCurrentPage,
    totalPages,
    bookings,
    updatePaymentStatus,
    deleteBooking,
    fetchBookings
  } = useBookings(API);
  
  // Apply initial search query if provided and sync when cleared
  React.useEffect(() => {
    // If initialSearchQuery changes (including clearing), update local search
    if (initialSearchQuery !== searchQuery) {
      setSearchQuery(initialSearchQuery);
    }
  }, [initialSearchQuery]);
  
  // Handle local search clear
  const handleClearSearch = () => {
    setSearchQuery('');
    if (onClearSearch) {
      onClearSearch();
    }
  };

  // Handle import from Beds24
  const handleImportFromBeds24 = async () => {
    if (!window.confirm('Import all bookings from Beds24? This may take a few moments.')) {
      return;
    }
    
    try {
      const response = await fetch(`${API}/admin/bookings/import-beds24`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('admin_token')}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        const parts = [];
        if (data.imported > 0) parts.push(`${data.imported} new`);
        if (data.updated > 0) parts.push(`${data.updated} updated`);
        if (data.cancelled > 0) parts.push(`${data.cancelled} cancelled`);
        if (data.deleted > 0) parts.push(`${data.deleted} deleted`);
        if (data.unmapped_rooms > 0) parts.push(`${data.unmapped_rooms} skipped (unmapped rooms)`);
        const message = parts.length > 0 
          ? `Beds24 sync: ${parts.join(', ')}` 
          : 'All bookings already up to date';
        alert(message);
        
        // Refresh bookings list without page reload
        await fetchBookings();
      } else {
        const error = await response.json();
        alert(error.detail || 'Failed to import bookings from Beds24');
      }
    } catch (error) {
      console.error('Error importing from Beds24:', error);
      alert('Error importing from Beds24. Please check your Beds24 credentials in Settings.');
    }
  };

  // State for inventory sync
  const [syncingInventory, setSyncingInventory] = useState(false);
  
  // State for pending cleanup
  const [cleaningPending, setCleaningPending] = useState(false);
  const [pendingToClean, setPendingToClean] = useState([]);
  const [showCleanupDialog, setShowCleanupDialog] = useState(false);

  // Handle cleanup pending bookings - dry run first
  const handleCleanupPendingDryRun = async () => {
    setCleaningPending(true);
    try {
      const response = await fetch(`${API}/admin/bookings/cleanup-pending`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('admin_token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ hours_threshold: 24, dry_run: true })
      });
      
      if (response.ok) {
        const data = await response.json();
        setPendingToClean(data.bookings || []);
        setShowCleanupDialog(true);
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to check pending bookings');
      }
    } catch (error) {
      console.error('Error checking pending bookings:', error);
      toast.error('Error checking pending bookings');
    } finally {
      setCleaningPending(false);
    }
  };

  // Execute the actual cleanup
  const handleCleanupPendingExecute = async () => {
    setCleaningPending(true);
    try {
      const response = await fetch(`${API}/admin/bookings/cleanup-pending`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('admin_token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ hours_threshold: 24, dry_run: false })
      });
      
      if (response.ok) {
        const data = await response.json();
        toast.success(`Expired ${data.expired_count} pending bookings`);
        setShowCleanupDialog(false);
        setPendingToClean([]);
        await fetchBookings(); // Refresh the list
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to cleanup pending bookings');
      }
    } catch (error) {
      console.error('Error cleaning up pending bookings:', error);
      toast.error('Error cleaning up pending bookings');
    } finally {
      setCleaningPending(false);
    }
  };

  // Handle send payment reminder
  const handleSendPaymentReminder = async (bookingId, guestEmail) => {
    if (!window.confirm(`Send payment reminder to ${guestEmail}?`)) {
      return;
    }
    
    try {
      const response = await fetch(`${API}/admin/bookings/${bookingId}/send-payment-reminder`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('admin_token')}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        toast.success(data.message || 'Payment reminder sent!');
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to send reminder');
      }
    } catch (error) {
      console.error('Error sending payment reminder:', error);
      toast.error('Error sending payment reminder');
    }
  };

  // Handle sync inventory to Beds24
  const handleSyncInventoryToBeds24 = async () => {
    // Get date range - today to 1 year from now
    const today = new Date();
    const oneYearLater = new Date();
    oneYearLater.setFullYear(oneYearLater.getFullYear() + 1);
    
    const dateFrom = today.toISOString().split('T')[0];
    const dateTo = oneYearLater.toISOString().split('T')[0];
    
    if (!window.confirm(`Sync inventory to Beds24 for ${dateFrom} to ${dateTo}?\n\nThis will update room availability in Beds24 based on current bookings.`)) {
      return;
    }
    
    setSyncingInventory(true);
    
    try {
      const response = await fetch(`${API}/admin/beds24/sync-all-inventory`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('admin_token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ date_from: dateFrom, date_to: dateTo })
      });
      
      if (response.ok) {
        const data = await response.json();
        alert(`Inventory synced to Beds24!\n\n• Rooms synced: ${data.rooms_synced}\n• Total updates: ${data.total_updates}\n• Date range: ${data.date_range}${data.errors ? `\n• Errors: ${data.errors.length}` : ''}`);
      } else {
        const error = await response.json();
        alert(error.detail || 'Failed to sync inventory to Beds24');
      }
    } catch (error) {
      console.error('Error syncing inventory to Beds24:', error);
      alert('Error syncing inventory to Beds24. Please try again.');
    } finally {
      setSyncingInventory(false);
    }
  };

  // Handle reprocess booking (resend emails and push to Beds24)
  const handleReprocessBooking = async (bookingId, guestEmail) => {
    const confirmMessage = `Reprocess booking ${bookingId}?\n\nThis will:\n• Send confirmation email to guest (${guestEmail})\n• Send notification to admin/owner\n• Push booking to Beds24\n\nContinue?`;
    
    if (!window.confirm(confirmMessage)) {
      return;
    }
    
    try {
      const response = await fetch(`${API}/admin/bookings/${bookingId}/reprocess`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('admin_token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          send_guest_email: true,
          send_admin_email: true,
          push_to_beds24: true
        })
      });
      
      if (response.ok) {
        const result = await response.json();
        const successCount = result.actions.filter(a => a.status === 'success').length;
        const failCount = result.actions.filter(a => a.status !== 'success').length;
        
        let message = `Booking ${bookingId} reprocessed!\n\n`;
        result.actions.forEach(action => {
          const icon = action.status === 'success' ? '✅' : '❌';
          message += `${icon} ${action.action}: ${action.message}\n`;
        });
        
        alert(message);
      } else {
        const error = await response.json();
        alert(error.detail || 'Failed to reprocess booking');
      }
    } catch (error) {
      console.error('Error reprocessing booking:', error);
      alert('Error reprocessing booking. Please try again.');
    }
  };

  // Handle silent sync to Beds24 (no emails)
  const handleSilentSync = async (bookingId) => {
    if (!window.confirm(`Sync booking ${bookingId} to Beds24?\n\nThis will ONLY push to Beds24 - no emails will be sent.`)) {
      return;
    }
    
    try {
      console.log('Silent sync starting for:', bookingId);
      console.log('API URL:', `${API}/admin/bookings/${bookingId}/reprocess`);
      
      const response = await fetch(`${API}/admin/bookings/${bookingId}/reprocess`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('admin_token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          send_guest_email: false,
          send_admin_email: false,
          push_to_beds24: true
        })
      });
      
      console.log('Response status:', response.status);
      
      if (response.ok) {
        const result = await response.json();
        console.log('Response data:', result);
        const beds24Action = result.actions.find(a => a.action === 'beds24_push');
        
        if (beds24Action?.status === 'success') {
          alert(`✅ Booking ${bookingId} synced to Beds24 successfully!`);
          // Refresh the page to update the list
          window.location.reload();
        } else {
          alert(`❌ Failed to sync: ${beds24Action?.message || 'Unknown error'}`);
        }
      } else {
        const error = await response.json();
        console.error('Error response:', error);
        alert(error.detail || 'Failed to sync booking');
      }
    } catch (error) {
      console.error('Error syncing booking:', error);
      alert('Error syncing booking. Please try again.');
    }
  };

  // Handle add booking
  const handleAddBooking = () => {
    if (onAddBooking) {
      onAddBooking();
    } else {
      setShowBookingDialog(true);
    }
  };

  const { rooms } = useRooms(API);
  const { roomTypes, getRoomTypeName } = useRoomTypes(API);
  const { cars, carTypes } = useCars(API);
  
  // Dialog state
  const [showBookingDialog, setShowBookingDialog] = useState(false);
  const [editingBooking, setEditingBooking] = useState(null);
  const [bookingViewMode, setBookingViewMode] = useState(false);
  const [bookingForm, setBookingForm] = useState({
    room_id: '',
    check_in: '',
    check_out: '',
    guest_name: '',
    guest_email: '',
    guest_phone: '',
    guest_country: '',
    num_guests: 1,
    special_requests: '',
    payment_status: 'pending',
    car_id: null,
    car_start_date: '',
    car_end_date: ''
  });

  // Get room types for a booking
  const getBookingRoomTypes = (booking) => {
    if (!booking.room_ids || booking.room_ids.length === 0) return 'N/A';
    
    const roomTypeCounts = {};
    booking.room_ids.forEach(roomId => {
      const room = rooms.find(r => r.id === roomId);
      if (room) {
        const typeName = getRoomTypeName(room.room_type_id);
        roomTypeCounts[typeName] = (roomTypeCounts[typeName] || 0) + 1;
      }
    });
    
    return Object.entries(roomTypeCounts)
      .map(([type, count]) => `${count}x ${type}`)
      .join(', ') || 'N/A';
  };

  // Table columns
  const columns = [
    {
      key: 'guest_name',
      label: 'Guest',
      sortable: true,
      render: (name, booking) => (
        <div>
          <div className="text-sm font-semibold text-gray-900">{name}</div>
          <div className="text-xs text-gray-500">{booking.guest_email}</div>
          <div className="text-xs text-gray-500">{booking.guest_phone}</div>
        </div>
      )
    },
    {
      key: 'id',
      label: 'Booking ID',
      render: (id, booking) => {
        // Always show local booking ID, with Beds24 indicator if synced
        const isSyncedToBeds24 = booking.beds24_booking_ids && booking.beds24_booking_ids.length > 0;
        const isImportedFromBeds24 = booking.source === 'beds24' && !id?.startsWith('BK');
        
        // For imported bookings without local ID, show Beds24 ID
        const displayId = isImportedFromBeds24 ? booking.beds24_booking_ids?.[0] : id;
        const bgColor = isImportedFromBeds24 ? 'bg-orange-50' : 'bg-blue-50';
        const textColor = isImportedFromBeds24 ? 'text-orange-600' : 'text-blue-600';
        
        return (
          <div>
            <div className={`text-sm font-mono font-bold ${textColor} ${bgColor} px-3 py-1 rounded inline-block`}>
              {displayId}
            </div>
            {isSyncedToBeds24 && !isImportedFromBeds24 && (
              <div className="text-xs text-green-600 mt-1 flex items-center gap-1">
                <span>✓</span>
                <span>Synced to Beds24</span>
              </div>
            )}
            {isImportedFromBeds24 && (
              <div className="text-xs text-gray-500 mt-1 flex items-center gap-1">
                <span className="text-orange-600">🔗</span>
                <span>From Beds24</span>
              </div>
            )}
          </div>
        );
      }
    },
    {
      key: 'created_at',
      label: 'Created',
      sortable: true,
      render: (date) => {
        if (!date) return <span className="text-gray-400">-</span>;
        const createdDate = new Date(date);
        const now = new Date();
        const diffHours = Math.floor((now - createdDate) / (1000 * 60 * 60));
        const diffDays = Math.floor(diffHours / 24);
        
        let relativeTime;
        if (diffHours < 1) {
          relativeTime = 'Just now';
        } else if (diffHours < 24) {
          relativeTime = `${diffHours}h ago`;
        } else if (diffDays < 7) {
          relativeTime = `${diffDays}d ago`;
        } else {
          relativeTime = createdDate.toLocaleDateString();
        }
        
        return (
          <div className="text-xs">
            <div className="text-gray-700">{createdDate.toLocaleDateString()}</div>
            <div className="text-gray-400">{relativeTime}</div>
          </div>
        );
      }
    },
    {
      key: 'room_ids',
      label: 'Rooms',
      render: (roomIds, booking) => (
        <div>
          <div className="text-sm font-semibold text-gray-900 flex items-center gap-2">
            {getBookingRoomTypes(booking)}
            {booking.car_id && (
              <span title="Car rental included" className="text-blue-600">🚗</span>
            )}
            {booking.transfer_required && (
              <span 
                title={`Airport Transfer: ${booking.transfer_type || 'N/A'}${booking.arrival_flight_number ? `\nArrival: ${booking.arrival_flight_number} at ${booking.arrival_flight_time || 'N/A'}` : ''}${booking.departure_flight_number ? `\nDeparture: ${booking.departure_flight_number} at ${booking.departure_flight_time || 'N/A'}` : ''}`} 
                className="text-green-600 cursor-help"
              >
                ✈️
              </span>
            )}
          </div>
          <div className="text-xs text-gray-500">
            {roomIds?.length || 0} room{roomIds?.length !== 1 ? 's' : ''}
          </div>
        </div>
      )
    },
    {
      key: 'check_in',
      label: 'Check-in',
      sortable: true,
      render: (date) => <div className="text-sm text-gray-900">{formatDate(date)}</div>
    },
    {
      key: 'check_out',
      label: 'Check-out',
      render: (date) => <div className="text-sm text-gray-900">{formatDate(date)}</div>
    },
    {
      key: 'num_guests',
      label: 'Guests',
      render: (count) => (
        <div className="flex items-center text-sm text-gray-600">
          <Users className="w-4 h-4 mr-1" />
          {count}
        </div>
      )
    },
    {
      key: 'bed_preference',
      label: 'Bed',
      render: (preference) => {
        if (!preference) return <div className="text-xs text-gray-400">Not specified</div>;
        return (
          <div className="text-xs">
            {preference === 'double' ? (
              <span className="px-2 py-1 bg-blue-50 text-blue-700 rounded">🛏️ Double</span>
            ) : preference === 'twin' ? (
              <span className="px-2 py-1 bg-green-50 text-green-700 rounded">🛏️🛏️ Twin</span>
            ) : (
              <span className="text-gray-400">-</span>
            )}
          </div>
        );
      }
    },
    {
      key: 'total_amount',
      label: 'Amount',
      sortable: true,
      render: (amount) => (
        <div className="text-sm font-bold text-blue-600">{formatCurrency(amount)}</div>
      )
    },
    {
      key: 'payment_status',
      label: 'Status',
      sortable: true,
      render: (status, booking) => (
        <div>
          <select
            value={status}
            onChange={(e) => updatePaymentStatus(booking.id, e.target.value)}
            className={`text-xs font-semibold rounded px-2 py-1 border-0 cursor-pointer ${
              status === 'paid' ? 'bg-green-100 text-green-700' :
              status === 'deposit_paid' ? 'bg-blue-100 text-blue-700' :
              status === 'pending' ? 'bg-yellow-100 text-yellow-700' :
              status === 'pending_payment' ? 'bg-orange-100 text-orange-700' :
              status === 'expired' ? 'bg-gray-100 text-gray-700' :
              status === 'refunded' ? 'bg-purple-100 text-purple-700' :
              status === 'cancelled' ? 'bg-gray-100 text-gray-500' :
              'bg-red-100 text-red-700'
            }`}
          >
            <option value="pending">pending</option>
            <option value="deposit_paid">deposit_paid</option>
            <option value="paid">paid</option>
            <option value="pending_payment">pending_payment</option>
            <option value="failed">failed</option>
            <option value="refunded">refunded</option>
            <option value="cancelled">cancelled</option>
            <option value="expired">expired</option>
          </select>
          {/* Show reminder indicator */}
          {booking.payment_reminder_sent_at && (
            <div className="text-xs text-amber-600 mt-1 flex items-center gap-1" title={`Reminder sent: ${new Date(booking.payment_reminder_sent_at).toLocaleString()}`}>
              <Mail className="w-3 h-3" />
              <span>Reminded {booking.payment_reminder_count || 1}x</span>
            </div>
          )}
        </div>
      )
    },
    {
      key: 'actions',
      label: 'Actions',
      className: 'text-right',
      render: (_, booking) => {
        const reminderSentAt = booking.payment_reminder_sent_at 
          ? new Date(booking.payment_reminder_sent_at).toLocaleString() 
          : null;
        const reminderTitle = reminderSentAt 
          ? `Last reminder: ${reminderSentAt} (${booking.payment_reminder_count || 1} sent)` 
          : 'Send payment reminder email';
        
        return (
        <div className="flex items-center justify-end gap-1">
          {/* Send Payment Reminder - only for pending/failed local bookings */}
          {booking.id?.startsWith('BK') && ['pending', 'failed'].includes(booking.payment_status) && booking.guest_email && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => handleSendPaymentReminder(booking.id, booking.guest_email)}
              title={reminderTitle}
              className={`hover:bg-amber-50 ${booking.payment_reminder_sent_at ? 'text-amber-500' : 'text-amber-600 hover:text-amber-700'}`}
            >
              <Mail className="w-4 h-4" />
              {booking.payment_reminder_count > 0 && (
                <span className="ml-0.5 text-xs">{booking.payment_reminder_count}</span>
              )}
            </Button>
          )}
          {/* Silent Sync - only show for local bookings not yet synced */}
          {booking.id?.startsWith('BK') && !booking.beds24_booking_ids?.length && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => handleSilentSync(booking.id)}
              title="Sync to Beds24 (no emails)"
              className="text-green-600 hover:text-green-700"
            >
              <Upload className="w-4 h-4" />
            </Button>
          )}
          <Button
            variant="ghost"
            size="sm"
            onClick={() => handleReprocessBooking(booking.id, booking.guest_email)}
            title="Reprocess: Resend emails & sync to Beds24"
            className="text-blue-600 hover:text-blue-700"
          >
            <RefreshCw className="w-4 h-4" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => handleEdit(booking)}
          >
            <Edit className="w-4 h-4" />
          </Button>
          {(booking.status === 'cancelled' || booking.payment_status === 'cancelled' || booking.payment_status === 'failed' || booking.payment_status === 'expired') && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => handleRestore(booking.id)}
              className="text-green-600 hover:text-green-700"
              title="Restore booking"
              data-testid={`restore-booking-${booking.id}`}
            >
              <RefreshCcw className="w-4 h-4" />
            </Button>
          )}
          <Button
            variant="ghost"
            size="sm"
            onClick={() => handleDelete(booking.id)}
            className="text-red-600 hover:text-red-700"
          >
            <Trash2 className="w-4 h-4" />
          </Button>
        </div>
        );
      }
    }
  ];

  const handleEdit = (booking) => {
    setEditingBooking(booking);
    setBookingViewMode(true); // Start in view mode, user can click Edit button to enable editing
    setBookingForm({
      room_id: booking.room_ids?.[0] || '',
      check_in: booking.check_in || '',
      check_out: booking.check_out || '',
      guest_name: booking.guest_name || '',
      guest_email: booking.guest_email || '',
      guest_phone: booking.guest_phone || '',
      guest_country: booking.guest_country || '',
      num_guests: booking.num_guests || 1,
      special_requests: booking.special_requests || '',
      payment_status: booking.payment_status || 'pending',
      car_id: booking.car_id || null,
      car_start_date: booking.car_start_date || '',
      car_end_date: booking.car_end_date || ''
    });
    setShowBookingDialog(true);
  };

  const handleCloseDialog = () => {
    setShowBookingDialog(false);
    setEditingBooking(null);
    setBookingViewMode(false);
    setBookingForm({
      room_id: '',
      check_in: '',
      check_out: '',
      guest_name: '',
      guest_email: '',
      guest_phone: '',
      guest_country: '',
      num_guests: 1,
      special_requests: '',
      payment_status: 'pending',
      car_id: null,
      car_start_date: '',
      car_end_date: ''
    });
  };

  const handleDelete = async (bookingId) => {
    if (!window.confirm('Are you sure you want to delete this booking?')) return;
    await deleteBooking(bookingId);
  };

  const handleRestore = async (bookingId) => {
    if (!window.confirm('Restore this booking back to active status?')) return;
    try {
      const response = await fetch(`${API}/admin/bookings/${bookingId}/restore`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('admin_token')}`,
          'Content-Type': 'application/json'
        }
      });
      const data = await response.json();
      if (response.ok) {
        toast.success(`Booking ${bookingId} restored to ${data.payment_status}`);
        fetchBookings();
      } else {
        toast.error(data.detail || 'Failed to restore booking');
      }
    } catch (error) {
      toast.error('Error restoring booking');
    }
  };

  const handleClearFilters = () => {
    handleClearSearch();
    setSearchFilter('all');
  };

  // Search filters
  const searchFilters = [
    { value: 'all', label: 'All Fields' },
    { value: 'guest_name', label: 'Guest Name' },
    { value: 'guest_email', label: 'Guest Email' },
    { value: 'booking_id', label: 'Booking ID' }
  ];

  return (
    <div>
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-2xl font-bold">All Bookings</h2>
            <p className="text-sm text-gray-600 mt-1">
              {searchQuery 
                ? `${filteredBySource.length} booking${filteredBySource.length !== 1 ? 's' : ''} (search results for "${searchQuery}")` 
                : `${filteredBySource.length} booking${filteredBySource.length !== 1 ? 's' : ''} ${sourceFilter !== 'all' ? `(${sourceFilter === 'local' ? 'Local Only' : 'Beds24 Linked'})` : 'total'}`
              }
            </p>
          </div>
          <div className="flex gap-2 flex-wrap">
            <Button 
              variant="outline"
              className="border-green-600 text-green-600 hover:bg-green-50"
              onClick={handleSyncInventoryToBeds24}
              disabled={syncingInventory}
              data-testid="sync-inventory-beds24-btn"
            >
              <RefreshCcw className={`w-4 h-4 mr-2 ${syncingInventory ? 'animate-spin' : ''}`} />
              {syncingInventory ? 'Syncing...' : 'Sync Inventory to Beds24'}
            </Button>
            <Button 
              variant="outline"
              className="border-green-600 text-green-600 hover:bg-green-50"
              data-testid="export-bookings-excel-btn"
              onClick={async () => {
                try {
                  const token = localStorage.getItem('admin_token');
                  const response = await fetch(`${API}/admin/export/bookings/excel`, {
                    headers: { 'Authorization': `Bearer ${token}` }
                  });
                  if (!response.ok) throw new Error('Export failed');
                  const blob = await response.blob();
                  const url = window.URL.createObjectURL(blob);
                  const a = document.createElement('a');
                  a.href = url;
                  a.download = `bookings_export_${new Date().toISOString().slice(0,10)}.xlsx`;
                  a.click();
                  window.URL.revokeObjectURL(url);
                  toast.success('Bookings exported successfully');
                } catch (err) {
                  toast.error('Failed to export bookings');
                }
              }}
            >
              <Download className="w-4 h-4 mr-2" />
              Export Excel
            </Button>
            <Button 
              variant="outline"
              className="border-blue-600 text-blue-600 hover:bg-blue-50"
              onClick={handleImportFromBeds24}
            >
              <Download className="w-4 h-4 mr-2" />
              Import from Beds24
            </Button>
            <Button 
              className="bg-gradient-to-r from-blue-600 to-indigo-600"
              onClick={handleAddBooking}
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Booking
            </Button>
          </div>
        </div>

        {/* Status Tabs */}
        <div className="flex gap-1 mb-3 border-b border-gray-200">
          <button
            onClick={() => setStatusFilter('active')}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              statusFilter === 'active' 
                ? 'border-blue-600 text-blue-600' 
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
            data-testid="tab-active-bookings"
          >
            Active ({bookings.filter(b => b.status !== 'cancelled' && b.payment_status !== 'cancelled').length})
          </button>
          <button
            onClick={() => setStatusFilter('cancelled')}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              statusFilter === 'cancelled' 
                ? 'border-red-600 text-red-600' 
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
            data-testid="tab-cancelled-bookings"
          >
            Cancelled ({bookings.filter(b => b.status === 'cancelled' || b.payment_status === 'cancelled').length})
          </button>
        </div>

        {/* Source Filter Tabs */}
        <div className="flex gap-2 mb-4 flex-wrap items-center">
          <Button
            variant={sourceFilter === 'all' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setSourceFilter('all')}
            className={sourceFilter === 'all' ? 'bg-gray-800' : ''}
          >
            All ({bookings.length})
          </Button>
          <Button
            variant={sourceFilter === 'local' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setSourceFilter('local')}
            className={sourceFilter === 'local' ? 'bg-blue-600' : 'border-blue-300 text-blue-600'}
          >
            📍 Local ({bookings.filter(b => b.id?.startsWith('BK')).length})
          </Button>
          <Button
            variant={sourceFilter === 'beds24' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setSourceFilter('beds24')}
            className={sourceFilter === 'beds24' ? 'bg-orange-600' : 'border-orange-300 text-orange-600'}
          >
            🔗 Beds24 ({bookings.filter(b => !b.id?.startsWith('BK')).length})
          </Button>
          
          {/* Pending bookings count & cleanup button */}
          {bookings.filter(b => b.id?.startsWith('BK') && b.payment_status === 'pending').length > 0 && (
            <Button
              variant="outline"
              size="sm"
              onClick={handleCleanupPendingDryRun}
              disabled={cleaningPending}
              className="border-amber-500 text-amber-600 hover:bg-amber-50 ml-auto"
            >
              <Clock className={`w-4 h-4 mr-1 ${cleaningPending ? 'animate-spin' : ''}`} />
              {cleaningPending ? 'Checking...' : `⏳ Pending (${bookings.filter(b => b.id?.startsWith('BK') && b.payment_status === 'pending').length})`}
            </Button>
          )}
        </div>
        
        {/* Cleanup Pending Dialog */}
        {showCleanupDialog && (
          <div className="mb-4 p-4 bg-amber-50 border border-amber-200 rounded-lg">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5" />
              <div className="flex-1">
                <h4 className="font-semibold text-amber-800">Pending Bookings Cleanup</h4>
                <p className="text-sm text-amber-700 mt-1">
                  Found {pendingToClean.length} local booking{pendingToClean.length !== 1 ? 's' : ''} pending for more than 24 hours:
                </p>
                {pendingToClean.length > 0 && (
                  <ul className="mt-2 text-sm text-amber-700 space-y-1 max-h-32 overflow-y-auto">
                    {pendingToClean.map(b => (
                      <li key={b.id} className="flex justify-between">
                        <span>{b.id} - {b.guest_name}</span>
                        <span className="text-amber-500">{new Date(b.created_at).toLocaleDateString()}</span>
                      </li>
                    ))}
                  </ul>
                )}
                <p className="text-sm text-amber-600 mt-2">
                  These bookings will be marked as "expired" and will no longer block room inventory.
                </p>
                <div className="flex gap-2 mt-3">
                  <Button
                    size="sm"
                    variant="destructive"
                    onClick={handleCleanupPendingExecute}
                    disabled={cleaningPending}
                    className="bg-amber-600 hover:bg-amber-700"
                  >
                    {cleaningPending ? 'Processing...' : `Expire ${pendingToClean.length} Booking${pendingToClean.length !== 1 ? 's' : ''}`}
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => { setShowCleanupDialog(false); setPendingToClean([]); }}
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}
        
        {/* Search Status - Only show if there's an active search */}
        {searchQuery && (
          <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
                <span className="text-sm font-medium text-blue-900">
                  Searching for: "{searchQuery}"
                </span>
              </div>
              <Button 
                variant="ghost" 
                size="sm"
                onClick={handleClearSearch}
                className="text-blue-700 hover:text-blue-900"
              >
                Clear
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Search & Filters */}
      <div className="mb-6">
        <SearchFilter
          searchQuery={searchQuery}
          onSearchChange={setSearchQuery}
          searchPlaceholder="Search bookings..."
          filters={searchFilters}
          selectedFilter={searchFilter}
          onFilterChange={setSearchFilter}
          onClear={handleClearFilters}
        />
      </div>

      {/* Bookings Table */}
      <DataTable
        columns={columns}
        data={paginatedBookings}
        onSort={toggleSort}
        sortField={sortField}
        sortOrder={sortOrder}
        testIdPrefix="booking"
        emptyState={
          <div className="text-center text-gray-500">
            {searchQuery ? (
              <>
                <h3 className="text-lg font-medium text-gray-900 mb-2">No bookings found</h3>
                <p className="text-sm mb-4">
                  No bookings match "{searchQuery}". Try a different search term.
                </p>
                <button
                  onClick={() => setSearchQuery('')}
                  className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                >
                  Clear search
                </button>
              </>
            ) : (
              <>
                <h3 className="text-lg font-medium text-gray-900 mb-2">No bookings yet</h3>
                <p className="text-sm">Create your first booking to get started.</p>
              </>
            )}
          </div>
        }
      />

      {/* Pagination */}
      <TablePagination
        currentPage={currentPage}
        totalPages={totalPages}
        totalItems={bookings.length}
        itemsPerPage={50}
        onPageChange={setCurrentPage}
      />

      {/* Booking Dialog */}
      {showBookingDialog && (
        <BookingDialog
          showBookingDialog={showBookingDialog}
          setShowBookingDialog={setShowBookingDialog}
          editingBooking={editingBooking}
          setEditingBooking={setEditingBooking}
          bookingViewMode={bookingViewMode}
          setBookingViewMode={setBookingViewMode}
          bookingForm={bookingForm}
          setBookingForm={setBookingForm}
          bookingRoomTypeFilter="all"
          setBookingRoomTypeFilter={() => {}}
          resetBookingForm={handleCloseDialog}
          handleCreateBooking={async (e) => {
            e.preventDefault();
            handleCloseDialog();
          }}
          handleUpdateBooking={async (e) => {
            e.preventDefault();
            try {
              const response = await fetch(`${API}/admin/bookings/${editingBooking.id}`, {
                method: 'PUT',
                headers: {
                  'Content-Type': 'application/json',
                  'Authorization': `Bearer ${localStorage.getItem('admin_token')}`
                },
                body: JSON.stringify({
                  ...bookingForm,
                  room_ids: [bookingForm.room_id]
                })
              });
              
              if (response.ok) {
                toast.success('Booking updated successfully');
                handleCloseDialog();
                // Refresh bookings list
                window.location.reload();
              } else {
                const error = await response.json();
                toast.error(error.detail || 'Failed to update booking');
              }
            } catch (error) {
              console.error('Error updating booking:', error);
              toast.error('Error updating booking');
            }
          }}
          handleDeleteBooking={(bookingId) => {
            deleteBooking(bookingId);
            handleCloseDialog();
          }}
          rooms={rooms}
          roomTypes={roomTypes}
          cars={cars}
          currentBooking={editingBooking}
          getRoomTypeName={(id) => roomTypes.find(rt => rt.id === id)?.name || 'Unknown'}
          getCarTypeName={(id) => carTypes?.find(ct => ct.id === id)?.name || 'Unknown'}
          formatDate={formatDate}
          formatCurrency={formatCurrency}
        />
      )}
    </div>
  );
};